import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaCalendarCheck, FaChevronDown, FaChevronRight, FaEllipsisH } from "react-icons/fa";
import "./org_chart.css";
import api from "../services/api";
import Dropdown from "../../components/modal/dropdown";
import MonthYearPicker from "../../components/modal/monthyear_datepicker";
import {
  getEmployeeStatusLabel,
  getEmployeeStatusClass,
  getAttendanceStatusLabel,
  getAttendanceStatusClass,
} from "../../constants/constants";

const formatEmployeeStatus = (emp) => getEmployeeStatusLabel(emp?.status);
const formatAttendanceStatus = (record) => getAttendanceStatusLabel(record?.status);


const MANAGER_AUTOCOLLAPSE_THRESHOLD = 7;
const EMPLOYEE_VISIBLE_COUNT = 10;


const buildTree = (employees) => {
  const byId = {};
  employees.forEach((e) => {
    byId[e.id] = { ...e, children: [] };
  });

  const roots = [];
  employees.forEach((e) => {
    const mgrId =
      typeof e.reporting_manager === "object"
        ? e.reporting_manager?.id
        : e.reporting_manager;
    if (mgrId && byId[mgrId]) {
      byId[mgrId].children.push(byId[e.id]);
    } else {
      roots.push(byId[e.id]);
    }
  });

  const byName = (a, b) => (a.full_name || "").localeCompare(b.full_name || "");
  const sortRec = (node) => {
    node.children.sort(byName);
    node.children.forEach(sortRec);
  };
  roots.sort(byName);
  roots.forEach(sortRec);

  return roots;
};

/* ── Prune the tree down to nodes that match `predicate`, keeping any
   ancestor of a match so the tree shape (the reporting chain) stays
   intact — a match six levels down still shows the path down to it. ── */
const pruneTree = (nodes, predicate) => {
  const out = [];
  nodes.forEach((node) => {
    const children = pruneTree(node.children, predicate);
    if (predicate(node) || children.length > 0) {
      out.push({ ...node, children });
    }
  });
  return out;
};


const computeDefaultCollapsed = (nodes, collapsed = new Set()) => {
  const managers = nodes.filter((n) => n.children && n.children.length > 0);
  if (managers.length > MANAGER_AUTOCOLLAPSE_THRESHOLD) {
    managers.forEach((m) => collapsed.add(m.id));
  }
  nodes.forEach((n) => computeDefaultCollapsed(n.children || [], collapsed));
  return collapsed;
};

/* ── Small avatar used by the single shared card. ── */
const OcAvatar = ({ node }) =>
  node.photo_url || node.photo ? (
    <img src={node.photo_url || node.photo} alt={node.full_name} className="oc-node-avatar-img" />
  ) : (
    <div className="oc-node-avatar-letter">
      {(node.full_name || "?").charAt(0).toUpperCase()}
    </div>
  );

/* ── The one and only node card. Photo on the left, name in the gold
   bar, designation in the navy bar underneath it — same look for a
   manager or an individual contributor. Managers additionally get a
   small chevron/count toggle pinned to the card to expand/collapse
   their team. ── */
const OcCard = ({ node, onSelect, isManager, isOpen, onToggle }) => (
  <div className="oc-node">
    <div className="oc-node-body" onClick={() => onSelect(node)}>
      <div className="oc-node-photo">
        <OcAvatar node={node} />
      </div>
      <div className="oc-node-info">
        <div className="oc-node-name" title={node.full_name}>{node.full_name}</div>
        <div className="oc-node-role" title={node.designation_name || "Employee"}>
          {node.designation_name || "Employee"}
        </div>
      </div>
    </div>

    {isManager && (
      <button
        type="button"
        className={`oc-node-toggle${isOpen ? " is-open" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(node.id);
        }}
        title={isOpen ? "Collapse team" : `Show ${node.children.length} report(s)`}
      >
        {isOpen ? <FaChevronDown /> : <FaChevronRight />}
        <span className="oc-node-toggle-count">{node.children.length}</span>
      </button>
    )}
  </div>
);

const ManagerCard = ({ node, onSelect, isOpen, onToggle }) => (
  <OcCard node={node} onSelect={onSelect} isManager isOpen={isOpen} onToggle={onToggle} />
);

const EmployeeCard = ({ node, onSelect }) => (
  <OcCard node={node} onSelect={onSelect} isManager={false} />
);

/* ── Detects whether the children of `ref` have wrapped onto more
   than one line (i.e. more than one distinct offsetTop among them).
   Re-checks on resize so it stays correct as the window/container
   changes width. Used to keep a single-row manager group centered
   while left-aligning it as soon as it wraps to a second row. ── */
const useRowWrap = (ref, deps) => {
  const [wrapped, setWrapped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const check = () => {
      const children = Array.from(el.children);
      if (children.length < 2) {
        setWrapped(false);
        return;
      }
      const tops = new Set(children.map((child) => child.offsetTop));
      setWrapped(tops.size > 1);
    };

    check();

    const ro = new ResizeObserver(check);
    ro.observe(el);
    Array.from(el.children).forEach((child) => ro.observe(child));

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return wrapped;
};

/* ── Renders one sibling group (all children of a single parent, or
   the root list):
     - managers first, laid out left-to-right and centered as a row
       (one manager sits centered on its own, a second is added to
       its right, a third to that one's right, and so on — wrapping
       to a new row once they no longer fit). While everything fits
       on one line the row stays centered; the moment it wraps to a
       second (or later) line, the whole row switches to left-aligned
       so wrapped rows don't also get centered. Every manager in the
       row is connected to its parent with the classic org-chart stem
       + horizontal bridge line. Expanding a manager drops its own
       team directly beneath it, recursively.
     - employees after, as a connected vertical list capped to
       EMPLOYEE_VISIBLE_COUNT with a "+N more" row to reveal the rest. ── */
const SiblingGroup = ({
  nodes,
  groupKey,
  onSelect,
  collapsedIds,
  toggleCollapse,
  expandedGroups,
  toggleMoreGroup,
  isFiltering,
}) => {
  const rowRef = useRef(null);

  const managers = nodes ? nodes.filter((n) => n.children && n.children.length > 0) : [];
  const employees = nodes ? nodes.filter((n) => !(n.children && n.children.length > 0)) : [];

  const rowWrapped = useRowWrap(rowRef, [managers.length, groupKey]);

  if (!nodes || nodes.length === 0) return null;

  const employeesExpanded = isFiltering || expandedGroups.has(groupKey);
  const visibleEmployees = employeesExpanded
    ? employees
    : employees.slice(0, EMPLOYEE_VISIBLE_COUNT);
  const hiddenCount = employees.length - visibleEmployees.length;

  return (
    <div className="oc-level">
      {managers.length > 0 && (
        <div
          ref={rowRef}
          className={`oc-children-row${rowWrapped ? " oc-multiline" : ""}`}
        >
          {managers.map((node) => {
            const isOpen = isFiltering || !collapsedIds.has(node.id);
            return (
              <div key={node.id} className="oc-row-child">
                <ManagerCard
                  node={node}
                  onSelect={onSelect}
                  isOpen={isOpen}
                  onToggle={toggleCollapse}
                />
                {isOpen && (
                  <div className="oc-subtree">
                    <SiblingGroup
                      nodes={node.children}
                      groupKey={node.id}
                      onSelect={onSelect}
                      collapsedIds={collapsedIds}
                      toggleCollapse={toggleCollapse}
                      expandedGroups={expandedGroups}
                      toggleMoreGroup={toggleMoreGroup}
                      isFiltering={isFiltering}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {employees.length > 0 && (
        <ul className="oc-elist">
          {visibleEmployees.map((node) => (
            <li key={node.id} className="oc-elist-item">
              <EmployeeCard node={node} onSelect={onSelect} />
            </li>
          ))}

          {hiddenCount > 0 && (
            <li className="oc-elist-item oc-elist-more">
              <button
                type="button"
                className="oc-vmore-btn"
                onClick={() => toggleMoreGroup(groupKey)}
              >
                <FaEllipsisH />
                <span>+{hiddenCount} more</span>
              </button>
            </li>
          )}

          {employeesExpanded && employees.length > EMPLOYEE_VISIBLE_COUNT && (
            <li className="oc-elist-item oc-elist-more">
              <button
                type="button"
                className="oc-vmore-btn oc-vmore-btn-less"
                onClick={() => toggleMoreGroup(groupKey)}
              >
                <span>Show less</span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const OrgChart = () => {
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empDetails, setEmpDetails] = useState({ attendance: null, attendanceDetails: [], tasksSummary: null });
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [dirLoading, setDirLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deptFilter, setDeptFilter] = useState("All");
  const [activeTab, setActiveTab] = useState("overview");
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  /* ── Fetch ALL employees (unpaginated) to build the tree from ── */
  useEffect(() => {
    setDirLoading(true);
    api.get("employees/", { params: { page_size: 500 } })
      .then(res => {
        const data = res.data?.data || res.data?.results || res.data || [];
        const list = Array.isArray(data) ? data : [];
        setAllEmployees(list);
        const roots = buildTree(list);
        setCollapsedIds(computeDefaultCollapsed(roots));
      })
      .catch(() => setAllEmployees([]))
      .finally(() => setDirLoading(false));
  }, []);

  const currentMonthStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  /* ── Reset state when employee is selected ── */
  useEffect(() => {
    if (selectedEmp) {
      setActiveTab("overview");
      const now = new Date();
      setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    }
  }, [selectedEmp]);

  /* ── Fetch profile data when an employee or month is selected ── */
  useEffect(() => {
    if (selectedEmp && selectedMonth) {
      setLoading(true);
      Promise.all([
        api.get("attendance/summary/", { params: { employee_id: selectedEmp.id, month: selectedMonth } }).catch(() => ({ data: {} })),
        api.get("attendance/", { params: { employee: selectedEmp.id, month: selectedMonth, page_size: 31 } }).catch(() => ({ data: {} })),
        api.get("tasks/employee-summary/", { params: { employee_id: selectedEmp.id } }).catch(() => ({ data: {} }))
      ]).then(([attRes, detailsRes, taskRes]) => {
        setEmpDetails({
          attendance: attRes.data?.data || attRes.data || null,
          attendanceDetails: detailsRes.data?.data || detailsRes.data?.results || detailsRes.data || [],
          tasksSummary: taskRes.data?.data || null
        });
        setLoading(false);
      });
    }
  }, [selectedEmp, selectedMonth]);

  /* ── Department list for the filter dropdown ── */
  const departments = useMemo(() => {
    const set = new Set();
    allEmployees.forEach(e => {
      const d = e.department_name || (typeof e.department === "string" ? e.department : e.department?.name);
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [allEmployees]);

  const deptOptions = useMemo(() => ["All", ...departments], [departments]);

  const q = searchQuery.trim().toLowerCase();
  const isFiltering = q.length > 0 || deptFilter !== "All";

  /* ── Full tree, then pruned down to matches (+ their ancestor chain)
     whenever a search or department filter is active. While filtering,
     the whole matched tree renders expanded (see isFiltering in
     SiblingGroup) so results are never hidden behind a collapsed
     manager or a "+N more" row. ── */
  const fullTree = useMemo(() => buildTree(allEmployees), [allEmployees]);

  const tree = useMemo(() => {
    if (!isFiltering) return fullTree;
    const matchesFilter = (emp) => {
      const dept = emp.department_name || (typeof emp.department === "string" ? emp.department : emp.department?.name) || "";
      if (deptFilter !== "All" && dept !== deptFilter) return false;
      if (!q) return true;
      return (
        (emp.full_name || "").toLowerCase().includes(q) ||
        (emp.email || "").toLowerCase().includes(q) ||
        (dept || "").toLowerCase().includes(q) ||
        (emp.designation_name || "").toLowerCase().includes(q) ||
        (emp.emp_id || "").toLowerCase().includes(q)
      );
    };
    return pruneTree(fullTree, matchesFilter);
  }, [fullTree, isFiltering, deptFilter, q]);

  const toggleCollapse = (id) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleMoreGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  /* ── Overview Tab ── */
  const renderOverviewTab = () => {
    const att = empDetails.attendance;

    const totalMarked = (att?.present || 0) + (att?.absent || 0) + (att?.leave || 0) + (att?.half_day || 0);
    const attRate = totalMarked > 0 ? Math.round(((att?.present || 0) / totalMarked) * 100) : 0;

    return (
      <div className="oc-tab-content">
        <div className="profile-grid">
          <div className="profile-card">
            <label>Employee ID</label>
            <span>{selectedEmp.emp_id}</span>
          </div>
          <div className="profile-card">
            <label>Department</label>
            <span>{typeof selectedEmp.department === "object" ? selectedEmp.department?.name : selectedEmp.department_name || (typeof selectedEmp.department === "string" ? selectedEmp.department : "—")}</span>
          </div>
          <div className="profile-card">
            <label>Designation</label>
            <span>{typeof selectedEmp.designation === "object" ? selectedEmp.designation?.name : selectedEmp.designation_name || (typeof selectedEmp.designation === "string" ? selectedEmp.designation : "—")}</span>
          </div>
          <div className="profile-card">
            <label>Reporting Manager</label>
            <span>{selectedEmp.reporting_manager ? (typeof selectedEmp.reporting_manager === "object" ? (selectedEmp.reporting_manager.full_name || selectedEmp.reporting_manager.name || "Assigned") : selectedEmp.reporting_manager) : "None"}</span>
          </div>
        </div>

        <h4 className="oc-section-title">Key Performance Indicators</h4>
        <div className="oc-kpi-row">
          <div className={`oc-kpi-card ${attRate >= 80 ? "oc-kpi-good" : "oc-kpi-warn"}`}>
            <div className="oc-kpi-top">
              <div className="oc-kpi-icon"><FaCalendarCheck /></div>
              <div className="oc-kpi-titlewrap">
                <span className="oc-kpi-label">Attendance Rate</span>
                <span className="oc-kpi-sub">Current Month</span>
              </div>
              <strong className="oc-kpi-value">{attRate}%</strong>
            </div>
            <div className="oc-progress-track">
              <div className="oc-progress-fill" style={{ width: `${attRate}%` }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ── Attendance Tab ── */
  const renderAttendanceTab = () => {
    const att = empDetails.attendance;
    const details = empDetails.attendanceDetails || [];

    const statCards = [
      { label: "Present", value: att?.present, color: "var(--success)" },
      { label: "Absent", value: att?.absent, color: "var(--danger)" },
      { label: "Half Day", value: att?.half_day, color: "var(--warning)" },
      { label: "Leaves", value: att?.leave, color: "var(--info)" },
      { label: "WFH", value: att?.wfh, color: "#06b6d4" },
      { label: "Work Hours", value: att?.total_working_hours, color: "#8b5cf6" },
    ];

    return (
      <div className="oc-tab-content">
        <div className="oc-section-header">
          <h4 className="oc-section-titles" style={{ marginBottom: 0 }}>Monthly Attendance</h4>
         <MonthYearPicker
                      value={selectedMonth}
                      onChange={(val) => setSelectedMonth(val || "")}
                      placeholder="Select month"
                      maxDate={currentMonthStr}
                      className="oc-month-picker"
                    />
        </div>
        
        {!att ? (
          <p className="oc-empty-msg">No attendance data available for {selectedMonth}.</p>
        ) : (
          <div className="oc-att-grid">
            {statCards.map(s => (
              <div key={s.label} className="oc-stat-card" style={{ "--oc-stat-accent": s.color }}>
                <strong>{s.value ?? "—"}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        )}

        <h4 className="oc-section-title" style={{ marginTop: "24px", marginBottom: "12px" }}>Daily Records</h4>
        <div className="oc-att-table-wrapper">
          <table className="table oc-daily-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Work Hours</th>
              </tr>
            </thead>
            <tbody>
              {details.length > 0 ? (
                details.map(record => (
                  <tr key={record.id} className="oc-daily-record-row">
                    <td data-label="Date" className="oc-record-date">{record.date}</td>
                    <td data-label="Status" className="oc-record-status">
                      <span className={`emp-status ${getAttendanceStatusClass(record.status)}`}>
                        {formatAttendanceStatus(record)}
                      </span>
                    </td>
                    <td data-label="Check In" className="oc-record-in">
                      {record.check_in_time ? record.check_in_time.slice(0, 5) : "—"}
                    </td>
                    <td data-label="Check Out" className="oc-record-out">
                      {record.check_out_time ? record.check_out_time.slice(0, 5) : "—"}
                    </td>
                    <td data-label="Work Hours" className="oc-record-hours">
                      {record.working_hours_display || record.working_hours || "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" >
                     <div className="empty-state">
                  <img src="/images/attendance.svg" alt="No Attendance" className="empty-img" />
                  <p> No Daily Attendance Records Found</p>
                </div>
                   
                  </td>
                </tr>
                
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /* ── Render ── */
  return (
    <div className="oc-wrapper">
      <div className="filters-card single oc-search-card">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search employees by name, email, department, or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Dropdown
          options={deptOptions}
          value={deptFilter}
          onChange={setDeptFilter}
          placeholder="Department"
        />
      </div>

      {dirLoading ? (
        <div className="oc-loading">
          <span className="oc-spinner" />
          Loading employee directory…
        </div>
      ) : tree.length === 0 ? (
        <div className="empty-state">
          <img src="/images/noemployee.svg" alt="No employees" className="empty-img" />
          <p>No Employees Found</p>
        </div>
      ) : (
        <div className="oc-vtree-container">
          <SiblingGroup
            nodes={tree}
            groupKey="root"
            onSelect={setSelectedEmp}
            collapsedIds={collapsedIds}
            toggleCollapse={toggleCollapse}
            expandedGroups={expandedGroups}
            toggleMoreGroup={toggleMoreGroup}
            isFiltering={isFiltering}
          />
        </div>
      )}

      {selectedEmp && (
        <div className="popup-overlay oc-overlay" >
          <div
            className="popup-content premium-profile oc-profile sticky-shell"
            style={{ maxHeight: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="profile-header-card">
              <div className="avatar-wrapper">
                {selectedEmp.photo_url || selectedEmp.photo ? (
                  <img src={selectedEmp.photo_url || selectedEmp.photo} className="avatar-img" alt="" />
                ) : (
                  <div className="avatar-letter">
                    {(selectedEmp.full_name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="employee-info">
                <h2>{selectedEmp.full_name}</h2>
                <p className="employee-role">{selectedEmp.designation_name || "Employee"} · {selectedEmp.department_name || "—"}</p>
                <div className="employee-contact">
                  {selectedEmp.email && <span> {selectedEmp.email}</span>}
                  {selectedEmp.phone && <span> {selectedEmp.phone}</span>}
                </div>
                {selectedEmp.status && (
                  <span className={`emp-status ${getEmployeeStatusClass(selectedEmp.status)}`}>
                    {formatEmployeeStatus(selectedEmp)}
                  </span>
                )}
              </div>
              <button className="profile-close" onClick={() => setSelectedEmp(null)}>Back</button>
            </div>

            {loading ? (
              <div className="oc-loading" style={{ padding: "40px 0" }}>
                <span className="oc-spinner" />
                Loading profile data…
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div className="profile-tabs">
                  {["overview", "attendance"].map(tab => (
                    <button
                      key={tab}
                      className={activeTab === tab ? "active" : ""}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="popup-scroll-body">
                  {activeTab === "overview" && renderOverviewTab()}
                  {activeTab === "attendance" && renderAttendanceTab()}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgChart;